import React, { useEffect, useRef, useState } from 'react'
import { Mail } from 'lucide-react'
import { supabase } from '../lib/supabase'

export default function PaymentRequestEmail(props) {
  const { job, customer } = props
  const scope = JSON.stringify([job.id, job.workshop_id, job.customer_id, job.financial_revision_key, customer?.email])
  return <ScopedPaymentRequestEmail key={scope} {...props} />
}

function ScopedPaymentRequestEmail({ job, customer, canCreate, canSend, disabled, onResult, onBusy }) {
  const mounted = useRef(true)
  const working = useRef(false)
  const command = useRef(null)
  const busyCallback = useRef(onBusy)
  busyCallback.current = onBusy
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  useEffect(() => { mounted.current = true; return () => {
    mounted.current = false
    if (working.current) busyCallback.current?.(false)
  } }, [])
  if (!canCreate && !canSend) return null
  async function request(endpoint, body) {
    const { data } = await supabase.auth.getSession()
    if (!mounted.current) throw new Error('Job changed. Review the current job before sending.')
    if (!data?.session?.access_token) throw new Error('Please sign in again before requesting payment.')
    const res = await fetch('/.netlify/functions/' + endpoint, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${data.session.access_token}` }, body: JSON.stringify(body),
    })
    const value = await res.json().catch(() => ({}))
    if (!res.ok) throw Object.assign(new Error(value.error || 'Payment request could not be sent.'), { retryAllowed: value.payment_only_retry_allowed === true })
    if (!mounted.current) throw new Error('Job changed. Check the recorded delivery before sending again.')
    return value
  }
  async function send() {
    if (working.current || disabled) return
    working.current = true
    setBusy(true); setError(''); setNotice('')
    busyCallback.current?.(true)
    let generated = null
    let sent = null
    try {
      command.current ||= { id: crypto.randomUUID(), initial: Boolean(canCreate) }
      if (canCreate) generated = await request('create-payment-link', { jobId: job.id })
      sent = await request('send-payment-reminder', { jobId: job.id, to: customer.email,
        deliveryMode: 'payment_only', requestId: command.current.id, initial: command.current.initial })
      if (!mounted.current) return
      setNotice(sent.already_sent ? 'This payment request was already emailed. No duplicate sent.' : 'Payment request emailed. No report or photos attached.')
      command.current = null
    } catch (err) {
      if (mounted.current) {
        if (err.retryAllowed) command.current = null
        setError((generated ? 'Payment link ready, but email not confirmed. ' : '') + err.message)
      }
    } finally {
      working.current = false
      if (mounted.current) {
        setBusy(false)
        busyCallback.current?.(false)
      }
    }
    if (mounted.current && (generated || sent)) onResult?.({ generated, sent })
  }
  return <div className="payment-request-email">
    <button type="button" className="btn btn-primary btn-sm" onClick={send} disabled={disabled || busy || !customer?.email}
      title={!customer?.email ? 'Add a customer email first.' : 'Emails the payment amount and link without attaching a report or photos.'}>
      <Mail size={16} aria-hidden="true" style={{ flexShrink: 0 }} />
      {busy ? 'Preparing and sending...' : canCreate ? 'Generate & Email Payment Request' : 'Email Payment Request Only'}
    </button>
    {error && <div className="alert alert-error" role="alert" style={{ marginTop: 8 }}>{error}</div>}
    {notice && <div className="alert alert-success" role="status" style={{ marginTop: 8 }}>{notice}</div>}
  </div>
}
